import {
  Error,
  InternalAddress,
  Order,
  OrderDeliveryType,
  OrderListOrderClazz,
  OrderPackageSize,
  UpdateOrderRequest,
} from '../../types/orderTypes';
import Fetch from '../../utils/fetch';
import { settings } from '../../settings';
import dayjs from 'dayjs';

export function timeGapBetweenInMinutes(
  previousDeliverUntil: string,
  currentDeliverFrom: string
): number | boolean {
  if (!previousDeliverUntil || !currentDeliverFrom) {
    return false;
  }
  const until = dayjs(previousDeliverUntil).startOf('minute');
  const from = dayjs(currentDeliverFrom).startOf('minute');
  return from.diff(until, 'minute');
}

export function packageSizeAsText(order: Order): string {
  let packageSizeText = '-';
  switch (order.packageSize) {
    case OrderPackageSize.Small:
      packageSizeText = 'Small';
      break;
    case OrderPackageSize.Medium:
      packageSizeText = 'Medium';
      break;
    case OrderPackageSize.Large:
      packageSizeText = 'Large';
      break;
    case OrderPackageSize.Xxl:
      packageSizeText = 'XXL';
  }
  if (order.xxl) {
    return `XXL (marked by sender as ${packageSizeText.toLocaleLowerCase()})`;
  }
  return packageSizeText;
}

export function packageSize(order: Order): OrderPackageSize {
  if (order.xxl) {
    return OrderPackageSize.Xxl;
  }
  return order.packageSize;
}

export function updateOrderRequest(order: Order): UpdateOrderRequest {
  return {
    origin: {
      name: order.pickupName,
      email: order.pickupEmail,
      phoneCountryCode: order.pickupPhoneCountryCode,
      phoneNumber: order.pickupPhoneNumber,
      address: order.originAddress,
    },
    destination: {
      name: order.recipientName,
      email: order.recipientEmail,
      phoneCountryCode: order.recipientPhoneCountryCode,
      phoneNumber: order.recipientPhoneNumber,
      address: {
        city: order.recipientCity,
        country: order.recipientCountry,
        postalCode: order.recipientPostalCode,
        streetName: order.recipientStreetName,
        streetNumber: order.recipientStreetNumber,
      },
      deliveryWindow: {
        start: order.deliverFrom,
        end: order.deliverUntil,
      },
      verifications: {
        leaveAtDoorstep: order.leaveAtDoorstep,
      },
    },
    warehouse: {
      name: order.pickupName,
      email: order.pickupEmail,
      phoneCountryCode: order.pickupPhoneCountryCode,
      phoneNumber: order.pickupPhoneNumber,
      address: {
        city: order.warehouseAddress.city,
        country: order.warehouseAddress.country,
        postalCode: order.warehouseAddress.postalCode,
        streetName: order.warehouseAddress.streetName,
        streetNumber: order.warehouseAddress.streetNumber,
      },
    },
    product:
      order.deliveryType === OrderDeliveryType.Express ? 'EXPRESS' : 'DELIVERY',
    courierInstructions: order.noteForCourier,
    packagesNumber: order.packagesNumber,
    packageSize: order.packageSize,
  };
}

export async function changeOrderToHub(
  order: Order,
  hubAddress: InternalAddress
): Promise<boolean | Error[]> {
  const request: UpdateOrderRequest = updateOrderRequest(order);
  request.origin.address = {
    city: hubAddress.city,
    country: hubAddress.country,
    postalCode: hubAddress.postalCode,
    streetName: hubAddress.streetName,
    streetNumber: hubAddress.streetNumber,
  };

  const response = await Fetch.put({
    url: `${settings.newApiUrl}/order/${order.id}`,
    body: request,
  });

  if (response.ok) {
    return true;
  } else if (response.status < 500) {
    const responseErrors: Error[] = await response.json();
    return responseErrors;
  } else {
    return [
      {
        message: `Something went wrong changing order ${order.id} origin address to Hub.`,
        propertyPath: '',
      },
    ];
  }
}

interface RescheduleInfo {
  changedOrderIds?: string[];
  removedFromBundleIds?: string[];
  differentDateBundleIds?: string[];
  errors?: Error[];
}

export async function rescheduleOrdersToNewDate(
  orders: OrderListOrderClazz[],
  date: Date,
  sendSms: boolean,
  sendEmail: boolean,
  closeDeviation: boolean,
  closeCourierDeviations: boolean,
  removeFromBundle: boolean,
  acceptAnyDate: boolean,
  message: string
): Promise<RescheduleInfo> {
  try {
    const request = {
      orders: orders.map(order => order.id),
      newDate: dayjs(date).format('YYYY-MM-DD'),
      sendSms: sendSms,
      sendEmail: sendEmail,
      closeDeviation: closeDeviation,
      closeCourierDeviations: closeCourierDeviations,
      removeFromBundle: removeFromBundle,
      acceptAnyDate: acceptAnyDate,
      message: message,
    };

    const response = await Fetch.put({
      url: `${settings.newApiUrl}/order/reschedule`,
      body: request,
    });

    if (response.ok) {
      return await response.json();
    } else if (response.status < 500) {
      const responseErrors: Error[] = await response.json();
      return { errors: responseErrors };
    } else {
      return {
        errors: [
          {
            message: `Something went wrong on the backend side setting new date.`,
            propertyPath: '',
          },
        ],
      };
    }
  } catch (e) {
    console.log('Error putting new Date', e);
    return {
      errors: [
        {
          message: `Something went wrong.`,
          propertyPath: '',
        },
      ],
    };
  }
}

interface ActionResult {
  success: boolean;
  error?: string;
}

export const cancelListOfOrders = async (
  orderIds: string[],
  note: string
): Promise<ActionResult> => {
  try {
    const request = {
      orderIds: orderIds,
      note: note,
    };
    const response = await Fetch.put({
      url: `${settings.newApiUrl}/order/mark-as-cancelled`,
      body: request,
    });
    if (response.ok) {
      return {
        success: true,
      };
    } else {
      return {
        success: false,
        error: `mark-as-cancelled returned ${response.status}`,
      };
    }
  } catch (e) {
    return {
      success: false,
      error: e.message,
    };
  }
};

export const markMultipleOrdersAsReady = async (
  orderIds: string[],
  allowUnCancelling: boolean
) => {
  return await Fetch.put({
    url: `${settings.newApiUrl}/order/mark-as-ready`,
    body: {
      orderIds: orderIds,
      allowUncancelling: allowUnCancelling,
    },
  });
};
